import { OveraLoggerOptions } from '@overa/shared';
import { environment } from 'src/environments/environment';

/**
 * Configuración por defecto del logger de la aplicación.
 *
 * Vive en su propio fichero porque la consumen dos sitios en momentos
 * distintos: `main.ts` la aplica antes de arrancar Angular (para que ninguna
 * traza del bootstrap se escape del filtro) y `app.config.ts` la vuelve a
 * aplicar, ya mezclada con lo que llegue del servidor, cuando Init carga la
 * configuración.
 *
 * Para subir el detalle de un módulo concreto sin recompilar, desde la consola
 * del navegador:
 *
 *     overaLogger.level("debug")
 *     overaLogger.scope("DynamicStudio", "trace")
 *     overaLogger.dump()
 */
export const LOGGER_DEFAULTS: OveraLoggerOptions = {
  // CAPTURA ANCHA, ENVÍO ESTRECHO, CONSOLA CALLADA
  //
  // En producción se captura `info` aunque nadie lo vaya a ver: el búfer circular es lo
  // que `OveraErrorHandler` adjunta a cada error como migas de pan, y con el umbral en
  // `warn` esas migas solo contenían otros avisos. El volumen enviado no depende de este
  // umbral sino del `minLevel` del sink (`overa-shared.otlpLogs.minLevel`).
  level: environment.production ? 'info' : 'debug',

  // En producción la consola va callada. Se recupera con `?debug=1`, con
  // Ctrl+Alt+Shift+L o con `overaLogger` desde DevTools (ver `applyDebugMode`).
  // Silencia lo NUESTRO: los errores del propio navegador (recursos, CSP, CORS, red) no
  // los puede ocultar ningún JavaScript.
  console: !environment.production,

  // Somete al umbral las llamadas directas a `console.*` (las hay a miles en la
  // UILib) sin tener que migrarlas. Dos modos, y la diferencia importa:
  //
  //  - en desarrollo, `"filter"` reasigna los métodos de `console` al nativo o a
  //    un noop, sin función intermedia, así que **DevTools sigue señalando el
  //    fichero que escribió la traza**. Con el envoltorio, todas las líneas
  //    apuntarían a `logger.ts` y se pierde el clic para saltar al código.
  //  - en producción, `"full"` las envuelve para que queden en el búfer y lleguen
  //    a los sinks: ahí no hay nadie mirando la consola y lo que vale son las
  //    migas de pan que acompañan a un error.
  captureConsole: environment.production ? 'full' : 'filter',

  // Lo mismo, pero para las llamadas AL LOGGER (`log.warn(...)`): el envoltorio emite el
  // `console.*` real desde `logger.ts`, así que DevTools atribuía ahí todas esas líneas.
  // Con esto los niveles devuelven el nativo pre-enlazado y la llamada sale del fichero
  // que la escribió. Solo en desarrollo, y con la misma renuncia que `"filter"`: esas
  // trazas no pasan por el búfer ni por los sinks.
  devtoolsAttribution: !environment.production,

  // Distintivo de nivel, ámbito alineado y `+Δms` desde la traza anterior.
  pretty: true,

  // Últimas trazas retenidas en memoria; son las que OveraErrorHandler adjunta
  // como contexto cuando algo revienta.
  bufferSize: 200,

  // Umbrales por módulo, con herencia por prefijo (`"a:b"` hereda de `"a"`).
  scopes: {},
};
